import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { getAzAccount, getCallerObjectId } from "../../lib/auth.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors } from "../../lib/output.js";
import {
  buildPreflightBanner, checkAppRegCapability, checkSubScopeAdmin, registerContainerInstance,
} from "../../lib/bootstrap-preflight.js";

export class BootstrapPreflightCommand extends M8tCommand {
  static paths = [["bootstrap", "preflight"]];
  static usage = Command.Usage({
    description: "Loudly verify you can install m8t-stack (Owner/UAA + directory admin) and hard-stop if not.",
    details:
      "Step 1 of `m8t bootstrap`. Prints an unmissable admin-credentials notice, then checks: Owner or User Access Administrator at subscription scope (hard requirement), directory-admin capability to register the app (or pass --client-id), and registers Microsoft.ContainerInstance. Exits non-zero with the exact failing check + remedy.",
    examples: [
      ["Run the preflight", "$0 bootstrap preflight"],
      ["BYO app registration (directory guests)", "$0 bootstrap preflight --client-id <appId>"],
    ],
  });

  clientId = Option.String("--client-id");
  subscription = Option.String("--subscription");

  async executeCommand(): Promise<number> {
    const clientId = typeof this.clientId === "string" ? this.clientId : undefined;
    const account = await getAzAccount();
    const subscriptionId = (typeof this.subscription === "string" ? this.subscription : undefined) ?? account.subscriptionId;
    const oid = await getCallerObjectId();

    this.context.stdout.write(buildPreflightBanner({ upn: account.upn, tenantId: account.tenantId, subscriptionId }) + "\n");
    this.context.stdout.write("Checking your access…\n");

    const sub = await checkSubScopeAdmin(oid, subscriptionId);
    this.context.stdout.write(line(sub.ok, `${sub.role ?? "Owner/UAA"} at subscription scope`, "resource + role-assignment rights"));
    if (!sub.ok) {
      this.context.stderr.write(failBlock("You are NOT an Owner or User Access Administrator at subscription scope.", "Ask a subscription Owner to run this install — the managed identity grant is unavoidable."));
      throw new LocalCliError({ code: "BOOTSTRAP_NOT_SUB_ADMIN", message: "Caller is not Owner/User Access Administrator at subscription scope." });
    }

    if (!clientId) {
      const appReg = await checkAppRegCapability(oid);
      this.context.stdout.write(line(appReg.ok, "Directory admin — can register the m8t-stack app", "first-run app registration"));
      if (!appReg.ok) {
        this.context.stderr.write(failBlock(
          appReg.inconclusive
            ? "Couldn't verify your directory admin role (Microsoft Graph denied the read — common for guest accounts)."
            : "You don't hold a directory admin role (Application / Cloud Application / Global Administrator).",
          "Re-run with --client-id <appId> using a ready app registration from an admin.",
        ));
        throw new LocalCliError({ code: "BOOTSTRAP_NO_APP_REG_CAP", message: "Caller cannot register the app; supply --client-id." });
      }
    } else {
      this.context.stdout.write(line(true, `Using your app registration ${clientId}`, "--client-id supplied; skipping the directory-admin check"));
    }

    await registerContainerInstance(subscriptionId);
    this.context.stdout.write(line(true, "Microsoft.ContainerInstance registered", "installer substrate"));

    this.context.stdout.write(
      `\n${colors.success("✅ Preflight passed.")} Here is the ONE consent step you'll clear, then walk away:\n` +
      `     1. The az login you just did.            ${colors.dim("← that's it for now.")}\n` +
      colors.dim("   Everything else (app registration, the installer identity + its roles,\n   the cloud installer itself) runs automatically and cleans itself up.\n") +
      `\n${colors.hint("next:")} m8t bootstrap launch --location <region>${clientId ? ` --client-id ${clientId}` : ""}\n`,
    );
    return 0;
  }
}

function line(ok: boolean, label: string, note: string): string {
  const mark = ok ? colors.success("✓") : colors.error("✗");
  return `  ${mark} ${label.padEnd(50)} ${colors.dim(`(${note})`)}\n`;
}
function failBlock(why: string, remedy: string): string {
  return (
    `\n${colors.error("⛔ CANNOT PROCEED")}\n` +
    `${colors.error("  " + why)}\n` +
    `  ${colors.hint("remedy:")} ${remedy}\n` +
    `  ${colors.hint("Nothing has been created. Re-run after fixing the above.")}\n\n`
  );
}
